import { useAuth0 } from "@auth0/auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { customClient } from "@utils/helpers";
import { useEffect, useMemo } from "react";

import { SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { GetSessionClientsDocument, SessionLearnerTypeEdge } from "@api/graphql/types-and-hooks";

export const useAllSessionClients = (enabled: boolean = true) => {
  const { getAccessTokenSilently } = useAuth0();

  const query = useInfiniteQuery({
    queryKey: ["getSessionClients"],
    queryFn: async ({ pageParam = { after: null } }) => {
      const token = await getAccessTokenSilently();
      const response = await customClient({
        token,
        document: GetSessionClientsDocument,
        variables: { after: pageParam.after },
      });

      return response.sessionLearnersWithSessions;
    },
    initialPageParam: { after: null },
    getNextPageParam: (lastPage) => {
      if (lastPage?.pageInfo?.hasNextPage) {
        return { after: lastPage.pageInfo.endCursor };
      }
      return undefined;
    },
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { hasNextPage, fetchNextPage, isFetchingNextPage } = query;

  // Auto-fetch all pages until done
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten into a single list of clients
  const clients: SearchableDropdownItem[] = useMemo(() => {
    return (
      query.data?.pages.flatMap(
        (page) =>
          page?.edges?.map((item: SessionLearnerTypeEdge) => ({
            id: item?.node?.sessionLearners?.id || "",
            label: `${item?.node?.sessionLearners?.learnermetadata?.firstName || ""} ${item?.node?.sessionLearners?.learnermetadata?.lastName || ""}`,
          })) || [],
      ) || []
    );
  }, [query.data]);

  return {
    clients,
    isLoading: query.isLoading,
    isFetchingNextPage,
  };
};
